import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Megaphone, FileVideo, Wallet, AtSign, CreditCard, Bell, User, LogOut, Plus, ExternalLink, AlertTriangle, Copy, CheckCircle2,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { api, apiError } from "@/lib/api";
import { money, fmtViews, fmtDate, timeAgo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo, StatusChip, PlatformPill, BudgetBar } from "@/components/brand";
import SubmitClip from "@/components/SubmitClip";
import { toast } from "sonner";

const NAV = [
  ["overview", "Overview", LayoutDashboard],
  ["campaigns", "My campaigns", Megaphone],
  ["submissions", "Submissions", FileVideo],
  ["earnings", "Earnings", Wallet],
  ["payment", "Payment", CreditCard],
  ["accounts", "Accounts", AtSign],
  ["notifications", "Notifications", Bell],
  ["profile", "Profile", User],
];

const MOBILE_NAV = ["overview", "submissions", "earnings", "payment", "accounts"];

export default function ClipperDashboard() {
  const [tab, setTab] = useState("overview");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [myCampaigns, setMyCampaigns] = useState([]);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/me/campaigns").then((r) => setMyCampaigns(r.data.campaigns || [])).catch(() => {}); }, [tab]);

  const Item = ({ id, label, Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`clipper-nav-${id}`} className="focus-ring" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer", width: "100%", textAlign: "left", fontSize: 14, background: tab === id ? "rgba(42,79,214,.16)" : "transparent", color: tab === id ? "var(--text)" : "var(--sub)" }}>
      <Icon size={17} /> <span className="hidden md:inline">{label}</span>
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex" }}>
      {/* desktop sidebar */}
      <aside className="hidden md:flex" style={{ width: 230, borderRight: "1px solid var(--border)", padding: 16, flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
        <div onClick={() => nav("/")} style={{ cursor: "pointer", marginBottom: 20 }}><Logo /></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>{NAV.map(([id, l, I]) => <Item key={id} id={id} label={l} Icon={I} />)}</div>
        <button onClick={async () => { await logout(); nav("/"); }} className="focus-ring" style={{ display: "flex", gap: 10, padding: "10px 12px", background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 14 }}><LogOut size={17} /> Log out</button>
      </aside>

      <main style={{ flex: 1, padding: "20px", paddingBottom: 90, maxWidth: 980, margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, margin: 0 }}>{NAV.find((n) => n[0] === tab)[1]}</h1>
            <p style={{ color: "var(--muted)", fontSize: 13, margin: "2px 0 0" }}>Hi {user?.name}</p>
          </div>
          <button className="btn-brand focus-ring" onClick={() => setSubmitOpen(true)} data-testid="clipper-submit-clip" style={{ padding: "9px 15px", fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}><Plus size={15} /> Submit clip</button>
        </div>

        {tab === "overview" && <Overview goTab={setTab} />}
        {tab === "campaigns" && <MyCampaigns campaigns={myCampaigns} onSubmit={() => setSubmitOpen(true)} />}
        {tab === "submissions" && <Submissions />}
        {tab === "earnings" && <Earnings />}
        {tab === "accounts" && <Accounts />}
        {tab === "payment" && <Payment />}
        {tab === "notifications" && <Notifications />}
        {tab === "profile" && <Profile user={user} />}
      </main>

      {/* mobile bottom bar */}
      <nav className="md:hidden" style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--surface)", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-around", padding: "8px 4px", zIndex: 30 }}>
        {NAV.filter(([id]) => MOBILE_NAV.includes(id)).map(([id, l, I]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`clipper-tab-${id}`} style={{ background: "none", border: "none", color: tab === id ? "var(--accent-light)" : "var(--muted)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, fontSize: 10, cursor: "pointer" }}>
            <I size={19} />{l.split(" ")[0]}
          </button>
        ))}
      </nav>

      <SubmitClip open={submitOpen} onClose={() => setSubmitOpen(false)} campaigns={myCampaigns} />
    </div>
  );
}

function Card({ children, style }) { return <div className="surface" style={{ borderRadius: 12, padding: 16, ...style }}>{children}</div>; }
function Kpi({ label, value }) { return <Card><div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div><div style={{ fontSize: 22, fontWeight: 500, marginTop: 4 }}>{value}</div></Card>; }

function Overview({ goTab }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/me/summary").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!d) return <Loading />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!d.has_upi && (
        <button onClick={() => goTab("payment")} data-testid="overview-add-upi-link" className="surface focus-ring" style={{ borderRadius: 12, padding: 14, borderColor: "rgba(245,165,36,.3)", background: "rgba(245,165,36,.08)", color: "var(--warning)", fontSize: 13, textAlign: "left", cursor: "pointer", width: "100%" }}>
          Add your UPI ID to get paid →
        </button>
      )}
      <button onClick={() => goTab("campaigns")} data-testid="overview-my-campaigns-link" className="surface focus-ring md:hidden" style={{ borderRadius: 12, padding: 14, color: "var(--accent-light)", fontSize: 13, textAlign: "left", cursor: "pointer", width: "100%" }}>
        My campaigns →
      </button>
      <div style={{ fontSize: 12, color: "var(--muted)" }}>Views last updated: {d.last_tracked_at ? fmtDate(d.last_tracked_at) : "—"}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
        <Kpi label="Total earned" value={money(d.total_earned)} />
        <Kpi label="Paid" value={money(d.paid)} />
        <Kpi label="Unpaid (waiting)" value={money(d.balance < 0 ? d.balance : d.unpaid)} />
        <Kpi label="Total views" value={fmtViews(d.total_views)} />
        <Kpi label="Approval rate" value={d.approval_rate + "%"} />
      </div>
      {d.balance < 0 && <div className="surface" style={{ borderRadius: 12, padding: 12, color: "var(--danger)", fontSize: 13 }}>You have {money(-d.balance)} to be adjusted against future earnings.</div>}
      {d.needs_attention?.length > 0 && (
        <Card>
          <h4 style={{ margin: "0 0 10px", fontSize: 14 }}>Clips needing attention</h4>
          {d.needs_attention.map((s) => (
            <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 13, color: "var(--sub)" }}>
              <AlertTriangle size={15} color="var(--danger)" /> {s.title || s.url?.slice(0, 40)} — {s.rejection_reason}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function MyCampaigns({ campaigns, onSubmit }) {
  const [code, setCode] = useState("");
  const joinCode = async () => { try { await api.post("/campaigns/join-with-code", { code }); toast.success("Joined"); setCode(""); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, color: "var(--sub)" }}>Join a private campaign:</span>
        <input className="input-dark" placeholder="Invite code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="clipper-join-code" style={{ width: 180 }} />
        <button className="btn-outline focus-ring" onClick={joinCode} style={{ padding: "9px 16px" }}>Join with code</button>
      </Card>
      {campaigns.length === 0 && <Card><div style={{ color: "var(--muted)", textAlign: "center", padding: 20 }}>You haven't joined any campaigns yet.</div></Card>}
      {campaigns.map((c) => (
        <Card key={c.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div><div style={{ fontWeight: 500 }}>{c.title}</div><div style={{ fontSize: 12, color: "var(--muted)" }}>{c.brand_name}</div></div>
            {c.member_status === "pending" ? <StatusChip status="pending" /> : <button className="btn-brand focus-ring" onClick={onSubmit} style={{ padding: "8px 14px", fontSize: 13 }}>Submit clip</button>}
          </div>
          <div style={{ display: "flex", gap: 20, marginTop: 12, fontSize: 13, color: "var(--sub)" }}>
            <span>My views: <b style={{ color: "var(--text)" }}>{fmtViews(c.my_views)}</b></span>
            <span>My earnings: <b style={{ color: "var(--text)" }}>{money(c.my_earnings)}</b></span>
            <span>Remaining: <b style={{ color: "var(--text)" }}>{money(c.budget_remaining)}</b></span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Submissions() {
  const [subs, setSubs] = useState([]);
  const [resub, setResub] = useState(null);
  const [url, setUrl] = useState("");
  const load = () => api.get("/me/submissions").then((r) => setSubs(r.data.submissions || []));
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);
  const doResub = async (id) => { try { await api.post(`/submissions/${id}/resubmit`, { url }); toast.success("Resubmitted"); setResub(null); setUrl(""); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {subs.length === 0 && <Card><div style={{ color: "var(--muted)", textAlign: "center", padding: 20 }}>No submissions yet.</div></Card>}
      {subs.map((s) => (
        <Card key={s.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>{s.campaign_title}</div>
              <a href={s.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent-light)", fontSize: 14, display: "inline-flex", gap: 5, alignItems: "center" }}>{s.title || s.url?.slice(0, 44)} <ExternalLink size={12} /></a>
              <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 13, color: "var(--sub)" }}>
                <PlatformPill platform={s.platform} />
                <span>{fmtViews((s.stats || {}).views)} views</span>
                <span>earned {money(s.earned)}</span>
                <PayChip status={s.payment_status} />
                <span style={{ color: "var(--muted)" }}>{fmtDate(s.submitted_at)}</span>
              </div>
              {s.status === "rejected" && <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger)" }}>Reason: {s.rejection_reason}</div>}
              {s.flags?.length > 0 && <div style={{ marginTop: 6, fontSize: 12, color: "var(--warning)" }}>Flagged, being checked</div>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
              <StatusChip status={s.status} />
              {s.status === "rejected" && <button className="btn-outline focus-ring" onClick={() => setResub(resub === s.id ? null : s.id)} style={{ padding: "6px 12px", fontSize: 12 }}>Resubmit</button>}
            </div>
          </div>
          {resub === s.id && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input className="input-dark" placeholder="New clip link" value={url} onChange={(e) => setUrl(e.target.value)} />
              <button className="btn-brand focus-ring" onClick={() => doResub(s.id)} style={{ padding: "0 16px" }}>Send</button>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function Earnings() {
  const [d, setD] = useState(null);
  const load = () => api.get("/me/earnings").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!d) return <Loading />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12 }}>
        <Kpi label="Total earned" value={money(d.balance.total_earned)} />
        <Kpi label="Paid" value={money(d.balance.paid)} />
        <Kpi label="Unpaid" value={money(d.balance.balance < 0 ? d.balance.balance : d.balance.unpaid)} />
        <Kpi label="Balance" value={money(d.balance.balance)} />
      </div>
      <Card>
        <h4 style={{ margin: "0 0 10px", fontSize: 14 }}>By campaign</h4>
        {d.per_campaign.map((p, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 13 }}>
            <span>{p.campaign_title}</span><span>{fmtViews(p.views)} views · earned <b>{money(p.earned)}</b> · paid {money(p.paid)}</span>
          </div>
        ))}
      </Card>
      <Card>
        <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>Payment history</h4>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0 }}>Payments are made manually by Clipup. Once paid, it will show here.</p>
        {d.payment_history.length === 0 ? <div style={{ color: "var(--muted)", fontSize: 13 }}>No payments yet.</div> : d.payment_history.map((p) => (
          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid var(--border)", fontSize: 13, opacity: p.reversed ? 0.5 : 1 }}>
            <span>{fmtDate(p.paid_at)}{p.reference ? ` · ${p.reference}` : ""} {p.reversed && <span style={{ color: "var(--danger)" }}>(reversed)</span>}</span>
            <span><b>{money(p.amount)}</b> {!p.reversed && <span style={{ color: "var(--success)", fontSize: 11 }}>Paid</span>}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function PayChip({ status }) {
  const map = { paid: ["Paid", "var(--success)"], partly_paid: ["Partly paid", "var(--warning)"], unpaid: ["Unpaid", "var(--muted)"] };
  const [t, c] = map[status] || map.unpaid;
  return <span style={{ fontSize: 11, color: c }}>{t}</span>;
}

function Accounts() {
  const [accs, setAccs] = useState([]);
  const [platform, setPlatform] = useState("youtube");
  const [handle, setHandle] = useState("");
  const load = () => api.get("/me/accounts").then((r) => setAccs(r.data.accounts || []));
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/me/accounts", { platform, handle, profile_url: handle }); toast.success("Account added"); setHandle(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const verify = async (id) => { try { await api.post(`/me/accounts/${id}/verify`); toast.success("Verified"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { await api.delete(`/me/accounts/${id}`); load(); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select className="input-dark" value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ width: "auto" }}>
          <option value="youtube">YouTube</option><option value="instagram">Instagram</option><option value="tiktok">TikTok</option><option value="x">X</option>
        </select>
        <input className="input-dark" placeholder="@handle or channel link" value={handle} onChange={(e) => setHandle(e.target.value)} data-testid="account-handle" style={{ flex: 1, minWidth: 180 }} />
        <button className="btn-brand focus-ring" onClick={add} data-testid="account-add" style={{ padding: "10px 16px" }}>Add account</button>
      </Card>
      {accs.map((a) => (
        <Card key={a.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div><div style={{ fontWeight: 500 }}><PlatformPill platform={a.platform} /> {a.handle}</div>
              {!a.verified && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>Add this code to your bio/description: <code style={{ color: "var(--accent-light)" }}>{a.verification_code}</code></div>}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {a.verified ? <span style={{ color: "var(--success)", fontSize: 13, display: "flex", gap: 5, alignItems: "center" }}><CheckCircle2 size={15} /> Verified</span> : <button className="btn-outline focus-ring" onClick={() => verify(a.id)} data-testid={`verify-${a.id}`} style={{ padding: "6px 12px", fontSize: 12 }}>Verify</button>}
              <button onClick={() => del(a.id)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 12 }}>Remove</button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Payment() {
  const [upi, setUpi] = useState(null);
  const [form, setForm] = useState({ upi_id: "", upi_id_confirm: "", holder_name: "", password: "" });
  const [processing, setProcessing] = useState("");
  const load = () => { api.get("/me/upi").then((r) => setUpi(r.data.upi)); api.get("/settings/public").then((r) => setProcessing(r.data.payout_processing_text)); };
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.put("/me/upi", form); toast.success("UPI saved"); setForm({ upi_id: "", upi_id_confirm: "", holder_name: "", password: "" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 12 }}>Payments are made manually by Clipup. Once paid, it will show here.{processing ? ` Payouts are processed within ${processing}.` : ""}</div>
        {upi ? (
          <div style={{ fontSize: 15 }}>Current UPI: <b>{upi.masked}</b> ({upi.holder_name})</div>
        ) : <div style={{ color: "var(--muted)", fontSize: 13 }}>No UPI ID saved yet.</div>}
      </Card>
      <Card>
        <h4 style={{ margin: "0 0 12px", fontSize: 14 }}>{upi ? "Change UPI ID" : "Add UPI ID"}</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <input className="input-dark" placeholder="UPI ID (name@bank)" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} data-testid="upi-id" />
          <input className="input-dark" placeholder="Confirm UPI ID" value={form.upi_id_confirm} onChange={(e) => setForm({ ...form, upi_id_confirm: e.target.value })} data-testid="upi-confirm" />
          <input className="input-dark" placeholder="Account holder name" value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} data-testid="upi-holder" />
          {upi && <input className="input-dark" type="password" placeholder="Account password (required to change)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="upi-password" />}
          <button className="btn-brand focus-ring" onClick={save} data-testid="upi-save" style={{ padding: "11px 16px" }}>Save UPI details</button>
        </div>
      </Card>
    </div>
  );
}

function Notifications() {
  const [notes, setNotes] = useState([]);
  useEffect(() => { api.get("/me/notifications").then((r) => setNotes(r.data.notifications || [])); api.post("/me/notifications/read"); }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {notes.length === 0 && <Card><div style={{ color: "var(--muted)", textAlign: "center", padding: 20 }}>No notifications.</div></Card>}
      {notes.map((n) => (
        <Card key={n.id}><div style={{ display: "flex", justifyContent: "space-between" }}><div><div style={{ fontWeight: 500 }}>{n.title}</div><div style={{ fontSize: 13, color: "var(--sub)" }}>{n.body}</div></div><span style={{ fontSize: 11, color: "var(--muted)" }}>{timeAgo(n.created_at)}</span></div></Card>
      ))}
    </div>
  );
}

function Profile({ user }) {
  return <Card><div style={{ fontSize: 14 }}><div style={{ marginBottom: 8 }}>Name: <b>{user?.name}</b></div><div style={{ marginBottom: 8 }}>Email: <b>{user?.email}</b></div><div>Role: <b>{user?.role}</b></div></div></Card>;
}

function Loading() { return <div style={{ color: "var(--muted)", padding: 20 }}>Loading…</div>; }
